import React, { useCallback, memo, useState, useContext } from 'react';
import AddNewObjectButton from '../../AddNewObjectButton/AddNewObjectButton';
import UrvalButton, {
  IUrvalButtonProps,
} from '../../../../../../Components/Urval/Components/UrvalButton/UrvalButton';
import KolumnValjareDialog from '../../../../../../Components/KolumnValjare/Components/KolumnValjareDialog/KolumnValjareDialog';
import { useBoolState } from '../../../../../../Hooks/booleanHooks';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDMenu from '../../../../../../BaseComponents/Menu/EDMenu';
import EDMenuItem from '../../../../../../BaseComponents/Menu/EDMenuItem';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { RutinSettingsContext } from '../../RutinAvailabilityContext/RutinAvailabilityContext';
import {
  findBusinessColors,
  findBusinessMenuUnusedButtons,
} from '../../../Const/BusinessConfigs';
import { CollectionMenuDisabledReason } from './BusinessCollectionMenu';
import {
  findRoutineMenuCustomAddButton,
  findRoutineMenuCustomUrvalButton,
  findRoutineMenuUnusedButtons,
} from '../../../Const/Rutiner';
import type { IProps } from './BusinessCollectionMenu';

import LegendMenuButton from '../../../../../../Components/Legend/LegendMenuButton';
import { useExportToExcel } from '../../../../../../Components/ExportToExcel/ExportToExcel';

const emptyUrval = { pages: [] };
/**
Toolbar för att ställa in en Urval, Kolumner.. för en businesscollection
*/

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      container: {
        display: 'flex',
        borderTop: `1px solid ${theme.customProperties.Dialog.actionDivider}`,
        background: theme.customProperties.Dialog.actionsBg,
        color: theme.palette.grey[700],
        bottom: 0,
        left: 0,
        width: '100%',
        height: theme.customProperties.businessCollectionMenuSmallHeight,
        boxShadow: theme.customProperties.Shadows.topShadow,
        zIndex: 2,
      },
      association: {
        bottom: theme.customProperties.stepperBarHeight,
      },
      button: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      },
      listItem: {
        justifyContent: 'center',
      },
      menuText: {
        marginLeft: '16px',
      },
      overViewSwitchContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        padding: '16px 6px',
      },
      selectedMenuText: {
        fontWeight: 'bold',
      },
    } as const)
);

export const BusinessCollectionMenuSmall = (props: IProps) => {
  const { refetch } = props;
  const { classes, cx } = useStyles();
  const [open, onOpen, onClose] = useBoolState(false);
  const [tabValue, setTabValue] = useState(0);
  const [miscMenuAnchor, setMiscMenuAnchor] = useState<any>(undefined);
  const handleRefetch = useCallback(() => {
    refetch();
    setMiscMenuAnchor(undefined);
  }, [refetch]);
  const rutinSettingsContext = useContext(RutinSettingsContext);

  const exportToExcel = useExportToExcel({
    collectionName: props.collection.CollectionName,
    columns: props.columns,
    sortProperties: props.sortedData.properties,
    urval: props.urval,
    searchText: props.searchText,
    rows: props.rows,
  });

  const setMenuAnchor = (event: React.MouseEvent<HTMLElement>) => {
    setMiscMenuAnchor(event.currentTarget);
  };

  const businessColors = findBusinessColors(props.collection.CollectionName);

  let unusedButtons = undefined;
  if (props.collection.CollectionName === props.baseCollection.CollectionName)
    unusedButtons = findRoutineMenuUnusedButtons(
      rutinSettingsContext.activeRoutine?.routine.rutinKod
    );
  if (!unusedButtons)
    unusedButtons =
      findBusinessMenuUnusedButtons(props.collection.CollectionName) ?? [];

  let UseUrvalButton:
    | React.FC<React.PropsWithChildren<IUrvalButtonProps>>
    | undefined = undefined;
  if (props.collection.CollectionName === props.baseCollection.CollectionName)
    UseUrvalButton = findRoutineMenuCustomUrvalButton(
      rutinSettingsContext.activeRoutine?.routine.rutinKod
    );
  if (!UseUrvalButton) UseUrvalButton = UrvalButton;

  let CustomAddButton = undefined;
  if (props.collection.CollectionName === props.baseCollection.CollectionName)
    CustomAddButton = findRoutineMenuCustomAddButton(
      rutinSettingsContext.activeRoutine?.routine.rutinKod
    );

  const onVisningsButtonClick = useCallback(() => {
    setTabValue(0);
    onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCloseMiscMenu = useCallback(() => setMiscMenuAnchor(undefined), []);

  return (
    <>
      <div
        className={cx(classes.container, {
          [classes.association]: props.isAssociation,
        })}
      >
        {!unusedButtons.includes('urval') &&
          (props.urval || props.setUrval) && ( // Om det inte finns ett urval eller möjlighet att sätta det så är urvalsknappen inte relevant
            <div className={classes.button}>
              <UseUrvalButton
                urval={props.urval}
                setUrval={props.setUrval}
                collectionName={props.collection.CollectionName}
                refetch={props.refetch}
                captionButton
                routineID={rutinSettingsContext.activeRoutine?.routine.rutinID}
                errorDisable={rutinSettingsContext.hasRutinStartError}
                errorText={
                  rutinSettingsContext.hasRutinStartError
                    ? CollectionMenuDisabledReason
                    : undefined
                }
                setSortInfoFromSelectionOrder={
                  props.setSortInfoFromSelectionOrder
                }
              />
            </div>
          )}
        {!unusedButtons.includes('column') && (
          <div className={classes.button}>
            <EDButton
              label="Visning"
              color="inherit"
              icon={<EDIcon iconType="view_column" color="inherit" />}
              onClick={onVisningsButtonClick}
              paddingY={0.5}
              labelNotUpperCase
              captionLabel
              flat
              disabled={rutinSettingsContext.hasRutinStartError}
              disabledReason={CollectionMenuDisabledReason}
            />
          </div>
        )}
        {!unusedButtons.includes('add') && (
          <div className={classes.button}>
            {CustomAddButton != null ? (
              <CustomAddButton setNewObject={props.setNewObject} smallMenu />
            ) : props.setNewObject ? (
              <EDButton
                label="Lägg till"
                color="inherit"
                icon={<EDIcon iconType="add" color="inherit" />}
                onClick={props.setNewObject}
                paddingY={0.5}
                labelNotUpperCase
                captionLabel
                disabled={props.errorDisable}
                disabledReason={CollectionMenuDisabledReason}
                flat
              />
            ) : (
              <AddNewObjectButton
                collection={props.collection}
                onSave={handleRefetch}
                columns={props.columns}
                urval={props.urval || emptyUrval}
                baseCollection={props.baseCollection}
                baseId={props.baseId}
                captionButton
                hasRutinStartError={rutinSettingsContext.hasRutinStartError}
              />
            )}
          </div>
        )}
        <div className={classes.button}>
          <EDButton
            label="Mer"
            color="inherit"
            icon={<EDIcon iconType="more_vert" color="inherit" />}
            onClick={setMenuAnchor}
            paddingY={0.5}
            labelNotUpperCase
            captionLabel
            flat
            disabled={rutinSettingsContext.hasRutinStartError}
            disabledReason={CollectionMenuDisabledReason}
            testId="button-viewSelectMobile"
          />
        </div>
      </div>
      <EDMenu
        id="misc-menu"
        anchorEl={miscMenuAnchor}
        open={Boolean(miscMenuAnchor)}
        onClose={onCloseMiscMenu}
      >
        {!unusedButtons.includes('refresh') && (
          <EDMenuItem color="inherit" onClick={handleRefetch}>
            <EDIcon iconType="refresh" />
            <EDTypography variant="subtitle1" className={classes.menuText}>
              Uppdatera
            </EDTypography>
          </EDMenuItem>
        )}
        {!unusedButtons.includes('excel') && (
          <EDMenuItem
            color="inherit"
            onClick={() => {
              exportToExcel();
              setMiscMenuAnchor(undefined);
            }}
            disabled={props.datasetLength < 1}
            disabledReason={
              props.datasetLength < 1 ? 'Inget att exportera' : undefined
            }
          >
            <EDIcon iconType="excel_export" />
            <EDTypography variant="subtitle1" className={classes.menuText}>
              Exportera till Excel
            </EDTypography>
          </EDMenuItem>
        )}

        {props.customBusinessCollectionMenuItems?.map(customItem => {
          return (
            <EDMenuItem
              color="inherit"
              onClick={() => {
                customItem.onClick?.();
                onCloseMiscMenu();
              }}
              key={customItem.id}
              selected={customItem.selected}
              testId={customItem.id}
            >
              {customItem.iconType && <EDIcon iconType={customItem.iconType} />}

              <EDTypography
                variant="subtitle1"
                className={cx(classes.menuText, {
                  [classes.selectedMenuText]: customItem.selected,
                })}
              >
                {customItem.caption}
              </EDTypography>
            </EDMenuItem>
          );
        })}

        {businessColors.length > 0 && (
          <LegendMenuButton
            posts={businessColors}
            margin="none"
            inMobileMenu
            onClose={onCloseMiscMenu}
          />
        )}
      </EDMenu>
      {props.setColumns && (
        <KolumnValjareDialog
          valdaKolumner={props.columns}
          collectionName={props.collection.CollectionName}
          setValdaKolumner={props.setColumns}
          sortedData={props.sortedData}
          updateSortOrder={props.updateSortOrder}
          tabValue={tabValue}
          setTabValue={setTabValue}
          open={open}
          onClose={onClose}
          inAssociation={
            props.baseCollection.CollectionName !==
            props.collection.CollectionName
          }
        />
      )}
    </>
  );
};

export default memo(BusinessCollectionMenuSmall);
